import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Define question types
type QuestionType = "picture" | "identifyOddOneOut" | "trueFalse" | "fillInTheBlanks" | "multipleChoice";

interface PictureQuestion {
    question: string;
    options: { src: string; answer: boolean }[];
}

interface IdentifyOddOneOutQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface TrueFalseQuestion {
    question: string;
    correct: boolean;
}

interface FillInTheBlanksQuestion {
    question: string;
    correct: string;
    options: string[];
}


const LivingNonLivingWorksheet: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState<PictureQuestion | IdentifyOddOneOutQuestion | MultipleChoiceQuestion | TrueFalseQuestion | FillInTheBlanksQuestion | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const [questionBank, setQuestionBank] = useState<Record<QuestionType, (PictureQuestion | IdentifyOddOneOutQuestion | MultipleChoiceQuestion | TrueFalseQuestion | FillInTheBlanksQuestion)[]>>({
        picture: [],
        identifyOddOneOut: [],
        multipleChoice: [],
        trueFalse: [],
        fillInTheBlanks: []
    });

    const fetchQuestions = async () => {
        try {
            let multipleChoiceQuestion: MultipleChoiceQuestion[] = [];
            let identifyOddOneOutQuestion: IdentifyOddOneOutQuestion[] = [];
            let trueFalseQuestion: TrueFalseQuestion[] = [];
            let fillInTheBlanksQuestion: FillInTheBlanksQuestion[] = [];
            let pictureQuestionQuestion: PictureQuestion[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {

                const response = await fetch(process.env.REACT_APP_API_URL + 'LivingNonLivingTrueFalse.json');
                trueFalseQuestion = await response.json();

                const response1 = await fetch(process.env.REACT_APP_API_URL + 'LivingNonLivingMultipleChoice.json');
                multipleChoiceQuestion = await response1.json();

                const response2 = await fetch(process.env.REACT_APP_API_URL + 'LivingNonLivingIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = await response2.json();

                const response3 = await fetch(process.env.REACT_APP_API_URL + 'LivingNonLivingFillInTheBlanks.json');
                fillInTheBlanksQuestion = await response3.json();

                const response4 = await fetch(process.env.REACT_APP_API_URL + 'LivingNonLivingPictureQuestion.json');
                pictureQuestionQuestion = await response4.json();

            } else {
                const module = await import('../../../datas/LivingNonLivingTrueFalse.json');
                trueFalseQuestion = module.default;

                const module1 = await import('../../../datas/LivingNonLivingMultipleChoice.json');
                multipleChoiceQuestion = module1.default;

                const module2 = await import('../../../datas/LivingNonLivingIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = module2.default;

                const module3 = await import('../../../datas/LivingNonLivingFillInTheBlanks.json');
                fillInTheBlanksQuestion = module3.default;

                const module4 = await import('../../../datas/LivingNonLivingPictureQuestion.json');
                pictureQuestionQuestion = module4.default;
            }
            setQuestionBank({
                picture: pictureQuestionQuestion,
                identifyOddOneOut: identifyOddOneOutQuestion,
                multipleChoice: multipleChoiceQuestion,
                trueFalse: trueFalseQuestion,
                fillInTheBlanks: fillInTheBlanksQuestion,
            });
        } catch (error) {
            console.error("Error loading questions:", error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Living and Non-Living Things for UKG Kids | Mama’s Guidance"; // Set page title
    }, []);

    useEffect(() => {
        if (questionBank.identifyOddOneOut.length || questionBank.multipleChoice.length || questionBank.trueFalse.length || questionBank.fillInTheBlanks.length) {
            getRandomQuestion();
        }
    }, [questionBank]);

    const getRandomQuestion = () => {
        const questionTypes = Object.keys(questionBank) as QuestionType[];
        const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const randomQuestion = questionBank[randomType][Math.floor(Math.random() * questionBank[randomType].length)];
        setCurrentQuestion(randomQuestion);
        setSelectedOption(null);
    };

    const handleAnswerSubmit = (option: any) => {
        setSelectedOption(option);
        if (!currentQuestion) return;

        if ("correct" in currentQuestion) {
            if (option === currentQuestion.correct) {
                showNextQuestion();
                setIsCorrect(true);
            }
        } else if ("options" in currentQuestion) {
            // For picture-based questions
            if ((currentQuestion as PictureQuestion).options.find((opt) => opt.src === option)?.answer) {
                showNextQuestion();
                setIsCorrect(true);
            }
        }
    };

    const showNextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setShowImage(false);
            setSelectedOption(null);
            getRandomQuestion();
            setIsCorrect(false)
        }, 5000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG children identify living and non-living things with fun activities and printable worksheets. Teach characteristics of life with interactive learning." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/environmental-science/living-non-living-things" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Living and Non-Living Things</h1>

                                <p>
                                    Understanding the difference between living and non-living things is an important environmental science topic for UKG children. This foundational concept builds observation, classification, and awareness of the world around them.
                                </p>

                                <h2>🧠 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Definition and characteristics of living things (need food, grow, move, breathe, reproduce)</li>
                                    <li>Examples of living and non-living things in daily life</li>
                                    <li>How to observe and classify objects based on life processes</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/environmental-science/traffic-rules-safety"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Traffic Rules & Safety</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/environmental-science/transport-vehicles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Transport & Vehicles</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>💡 Activities to Try at Home</h2>
                                <ul>
                                    <li>Nature walk: Ask your child to point out living and non-living things</li>
                                    <li>Play “Is it Alive?” game with household objects and pets</li>
                                    <li>Use storybooks and cartoons to reinforce the concepts</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Living and Non-Living Things</h1>

                            <p>
                                Understanding the difference between living and non-living things is an important environmental science topic for UKG children. This foundational concept builds observation, classification, and awareness of the world around them.
                            </p>

                            <h2>🧠 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Definition and characteristics of living things (need food, grow, move, breathe, reproduce)</li>
                                <li>Examples of living and non-living things in daily life</li>
                                <li>How to observe and classify objects based on life processes</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/environmental-science/traffic-rules-safety"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Traffic Rules & Safety</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/environmental-science/transport-vehicles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Transport & Vehicles</IonLabel>
                                </IonItem>
                            </div>

                            <h2>💡 Activities to Try at Home</h2>
                            <ul>
                                <li>Nature walk: Ask your child to point out living and non-living things</li>
                                <li>Play “Is it Alive?” game with household objects and pets</li>
                                <li>Use storybooks and cartoons to reinforce the concepts</li>
                            </ul>
                        </article>
                        {currentQuestion ? (
                            <>
                                <h3 className="mb-5">{currentQuestion.question}</h3>

                                {"options" in currentQuestion ? (
                                    // Handle multiple-choice or identify odd one out question
                                    typeof currentQuestion.options[0] != "object" ? (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            {(currentQuestion as IdentifyOddOneOutQuestion | MultipleChoiceQuestion).options.map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? isCorrect === true
                                                            ? "correct"
                                                            : "errorOptions"
                                                        : ""
                                                        }`}
                                                    key={index}>
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonRadioGroup>
                                    ) : (
                                        // Handle picture question
                                        <div className="row">
                                            {(currentQuestion as PictureQuestion).options.map((option, index) => (
                                                <div
                                                    className={`col-12 col-md-3 mb-5 `}
                                                    key={index}>
                                                    <img
                                                        src={process.env.REACT_APP_API_URL + option.src}
                                                        onClick={() => handleAnswerSubmit(option.src)}
                                                        className={`img-thumbnail ${selectedOption === option.src
                                                            ? isCorrect === true
                                                                ? "correct"
                                                                : "errorImgItems"
                                                            : ""
                                                            }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            height: '150px',
                                                            width: '150px'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) :
                                    <>
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value!)}>
                                            <IonItem
                                                className={`grid-item ${selectedOption === true
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={true} />
                                                <IonLabel>True</IonLabel>
                                            </IonItem>
                                            <IonItem
                                                className={`grid-item ${selectedOption === false
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={false} />
                                                <IonLabel>False</IonLabel>
                                            </IonItem>
                                        </IonRadioGroup>
                                    </>
                                }
                            </>
                        ) : (
                            <p>Loading question...</p>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default LivingNonLivingWorksheet;